import type { ReactNode } from "react";
import { AlertTriangle, Lock, RefreshCw } from "lucide-react";
import { ApiError } from "../api/client";
import { Button } from "./Button";
import { Spinner } from "./Spinner";

/**
 * The screen states §137 requires: loading, empty, error and permission-denied.
 *
 * <p>Centralised so no screen quietly renders nothing. A blank page is the worst of the outcomes,
 * because the reader cannot tell whether the system is broken, still working, or correctly showing
 * that there is nothing to see.
 */

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16" role="status" aria-live="polite">
      <Spinner />
      <span className="text-sm text-ink-500">{label}</span>
    </div>
  );
}

/**
 * Renders a failure in terms the reader can act on.
 *
 * <p>A 403 gets no retry button. The answer will be the same however many times it is asked, and
 * offering the action implies otherwise.
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (error instanceof ApiError && error.isForbidden) {
    return <PermissionDenied what="this" />;
  }

  const message = error instanceof Error ? error.message : "Something went wrong.";
  const traceId = error instanceof ApiError ? error.traceId : undefined;

  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-red-700">Could not load this</p>
          <p className="mt-0.5 text-sm text-red-600">{message}</p>
          {/* Quoted so a support request can find the exact call in the logs. */}
          {traceId && (
            <p className="mt-1.5 font-mono text-xs text-red-600/70">Reference: {traceId}</p>
          )}
        </div>
        {onRetry && (
          <Button variant="secondary" size="sm" icon={<RefreshCw className="h-4 w-4" />} onClick={onRetry}>
            Try again
          </Button>
        )}
      </div>
    </div>
  );
}

/** Shown in place of a screen the signed-in user may not see. */
export function PermissionDenied({ what }: { what: string }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
      <div className="flex items-start gap-3">
        <Lock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div>
          <p className="text-sm font-semibold text-amber-700">Not available to you</p>
          <p className="mt-0.5 text-sm text-amber-600">
            You do not have permission to view {what}. Nothing has been changed — ask an
            administrator if you think you should have access.
          </p>
        </div>
      </div>
    </div>
  );
}

/** An inline message inside a drawer or form, where a full error panel would be too heavy. */
export function InlineAlert({
  tone = "error",
  children,
}: {
  tone?: "error" | "info" | "success" | "warning";
  children: ReactNode;
}) {
  const tones = {
    error: "border-red-200 bg-red-50 text-red-700",
    info: "border-brand-200 bg-brand-50 text-brand-700",
    success: "border-emerald-200 bg-emerald-50 text-emerald-700",
    warning: "border-amber-200 bg-amber-50 text-amber-700",
  } as const;

  return (
    <div className={`rounded-lg border px-3 py-2 text-sm ${tones[tone]}`}>{children}</div>
  );
}
